define("diy/undo-redo/App",
    [
        'jquery',
        'diy/undo-redo/constants',
        'diy/util/helper',
        'diy/util/globalEvents'
    ],
    /**
     *
     * @param {jQuery} $
     * @param {module.undoRedo.constants} constants
     * @param {diy.util.Helper} helper
     * @param {diy.util.globalEvents} globalEvents
     * @returns {*}
     */
    function ($, constants, helper, globalEvents) {
        'use strict';
        var
            readyState = {
                READY: 1,
                DATABASE_INIT: 2,
                DEPENDENCIES: 3,
                HISTORY_INIT: 4
            },
            UndoRedo;


        /**
         * @class module.undoRedo.App
         * @template module.undoRedo.App
         * @lends {module.undoRedo.App.prototype}
         * @constructor
         */
        UndoRedo = Klazz.extend({

            readyState: 0,

            /** @type {module.undoRedo.Api} */
            api: null,
            /** @type {module.undoRedo.PouchAdapter} */
            database: null,
            /** @type {module.undoRedo.Settings} */
            settings: null,
            /** @type {module.undoRedo.History} */
            history: null,
            /** @type {module.undoRedo.SessionManager} */
            sessionManager: null,
            /** @type {module.undoRedo.Ui} */
            ui: null,
            /** @type {module.undoRedo.UiManager} */
            uiManager: null,
            /** @type {module.undoRedo.Storage} */
            storage: null,
            /** @type {module.undoRedo.FeatureSet} */
            features: null,
            /** @type {module.undoRedo.EntryFactory} */
            entryFactory: null,
            /** @type {module.undoRedo.HistoryEntry.Factory} */
            historyEntryFactory: null,
            /** @type {module.undoRedo.Executor.Main} */
            executor: null,
            /** @type {module.undoRedo.RemoteRequest} */
            backend: null,

            browserReload: {
                requested: false,
                url: null
            },

            isReady: function () {
                return this.readyState === readyState.READY;
            },
            /**
             *
             * @param {module.undoRedo.Api} api
             * @returns {module.undoRedo.App}
             */
            setApi: function (api) {
                this.api = api;
                return this;
            },

            /**
             *
             * @param {module.undoRedo.History} history
             * @param {module.undoRedo.PouchAdapter} database
             * @param {module.undoRedo.Storage} storage
             * @param {module.undoRedo.EntryFactory} entryFactory
             * @param {module.undoRedo.Ui} ui
             * @param {module.undoRedo.UiManager} uiManager
             * @param {module.undoRedo.Executor.Main} executor
             * @param {module.undoRedo.RemoteRequest} backend
             * @param {module.undoRedo.Settings.messages} messages
             * @returns {*}
             */
            init: function (history, database, storage, entryFactory, ui, uiManager, executor, backend, messages) {
                var deferred = Q.defer(), initChain = [], ini;
                this.history = history;
                this.database = database;
                this.storage = storage;
                this.entryFactory = entryFactory;
                this.ui = ui;
                this.uiManager = uiManager;
                this.executor = executor;
                this.backend = backend;
                try {

                    ini = this.settings.getIni();

                    /** trigger refresh on ui so the toolbar will be painted in browser
                     * before the actual data loads
                     */
                    this.ui.refresh();

                    /**
                     * Attach observer on history, to trigger ui.refresh when history.chains are changed
                     */
                    this.history.attachObserver(function (history, data) {
                        this.uiManager.handle(data);
                    }.bind(this), constants.observerTopics.HISTORY_CHAIN_READY);

                    /**
                     * clean up the local database due to the fact that not all users are using
                     * the logout feature; this prevents exceeding the 5MB limit on local storage use
                     */
                    initChain.push(this.clearLocalDataOnFirstLogin.bind(this));

                    /**
                     * Composing initiation sequence
                     */
                    initChain.push(
                        /**
                         * try to open local storage database
                         */
                        function () {
                            this.readyState = readyState.DATABASE_INIT;
                            return this.database.open();
                        }.bind(this)
                    );

                    initChain.push(
                        /**
                         * inject the obtained database to the objects that needs it
                         */
                        function (database) {
                            var dependencies = Q.defer();
                            this.readyState = readyState.DEPENDENCIES;

                            if (database !== null) {
                                database.init(ini.websiteId, ini.sessionId);
                            }

                            this.storage.init(database, ini.websiteId, ini.sessionId)
                                .then(function () {
                                    this.history.setStorage(this.storage);
                                    this.sessionManager.setStorage(this.storage);

                                    dependencies.resolve(true);
                                }.bind(this))
                                .catch(function (error) {
                                    dependencies.reject(error);
                                });

                            return dependencies.promise;
                        }.bind(this)
                    );

                    initChain.push(
                        /**
                         * initialize the history chains
                         */
                        function () {
                            this.readyState = readyState.HISTORY_INIT;

                            return this.sessionManager.recoverActiveSession()
                                .then(this.history.init.bind(this.history));

                        }.bind(this)
                    );



                    initChain.reduce(Q.when, Q(null))
                        .then(function (result) {
                            this.readyState = readyState.READY;
                            /**
                             * Proxy observer events to api
                             */
                            this.history.attachObserver(function (history, data, topic) {
                                this.api.notifyObservers(topic, data, history);
                            }.bind(this), constants.observerTopics.ALL);

                            deferred.resolve(result);
                        }.bind(this))
                        .catch(function (error) {
                            /**
                             * PouchDb gives error.status = 500 when browser is in private mode
                             */
                            if (error && error.status && error.status === 500) {
                                this.ui.showErrorBox(messages.errors.privateMode);
                                this.backend.logPrivateMode();
                                error.wasHandled = true;
                            } else {
                                this.ui.showErrorBox(messages.errors.generic);
                            }
                            deferred.reject(error);
                        }.bind(this));

                } catch (error) {
                    console && console.log && console.log('caught ERROR in app init', error);
                    this.ui.showErrorBox(messages.errors.generic);
                    deferred.reject(error);
                }
                return deferred.promise;
            },
            /**
             * Closes active session, reloads data from backend
             */
            requestServerSync: function requestServerSync() {
                this.history.executeWhenReady(function () {
                    return this.history.refresh();
                }.bind(this));
            },
            /**
             * @param {boolean} noWait
             * @returns {Promise|Q.Promise}
             */
            closeActiveSession: function closeActiveSession(noWait) {
                return this.sessionManager.closeSession(this.sessionManager.getActiveSession(), noWait);
            },
            /**
             * Resets local storage on first login
             *
             * @returns {Promise|Q.Promise}
             */
            clearLocalDataOnFirstLogin: function () {
                if (helper.isJustLoggedIn()) {
                    return this.cleanUp();
                } else {
                    return Q(true);
                }
            },
            /**
             * @constructs
             * @param {module.undoRedo.Settings} settings
             * @param {module.undoRedo.FeatureSet} features
             */
            initialize: function (settings, features) {
                this.settings = settings;
                this.features = features;
            },

            /**
             *
             * @param {module.undoRedo.SessionManager} sessionManager
             * @returns {module.undoRedo.App}
             */
            setSessionManager: function setSessionManager(sessionManager) {
                this.sessionManager = sessionManager;
                return this;
            },
            /**
             *
             * @param {module.undoRedo.HistoryEntry.Factory} historyEntryFactory
             * @returns {module.undoRedo.App}
             */
            setHistoryEntryFactory: function setHistoryEntryFactory(historyEntryFactory) {
                this.historyEntryFactory = historyEntryFactory;
                return this;
            },

            /**
             *
             * @param sessionId
             * @returns {Promise|Q.Promise}
             * @param {boolean} noWait
             */
            deleteEntriesWithUndoSession: function deleteEntriesWithUndoSession(sessionId, noWait) {
                if (noWait === true ) {
                    return this.history.executeDeleteUndoSession(sessionId);
                }
                return this.history.deleteUndoSession(sessionId);
            },

            /**
             * Called when an error occurred
             *
             *
             * @param error
             */
            onError: function (error) {
                if (error.message && error.message === constants.executionTimeoutMessage) {
                    // we could use another message in here, but is not in the scope
                    this.ui.showErrorBox();
                } else {
                    this.ui.showErrorBox();
                }

                globalEvents.trigger('error.diy-editor', {
                    message: 'UndoRedo exception: message = ' + error.message + ', stack = ' + error.stack
                });
            },

            /**
             * Adds HistoryEntry to undo list
             *
             * @param historyEntry
             * @param undoSession
             * @param sessionIndex
             * @private
             * @returns {Promise|Q.Promise}
             */
            addClientEntry: function addClientEntry(historyEntry, undoSession, sessionIndex) {
                var sequence = Q.defer();

                this.history.executeWhenReady(function () {
                    var
                        /** @type {module.undoRedo.Entry} */
                        Entry = this.entryFactory.wrap(historyEntry);

                    Entry
                        .setUndoSessionId(undoSession)
                        .setSessionIndex(sessionIndex)
                        .setType(constants.types.UNDO)
                    ;

                    this.history.addEntry(Entry)
                        .then(function (entryKey) {
                            sequence.resolve(entryKey);
                        })
                        .catch(function (error) {
                            sequence.reject(error);
                            this.onError(error);
                        });

                    return sequence.promise;
                }.bind(this));

                return sequence.promise;
            },
            /**
             * Creates a new undo-session; undo-sessions can be used to add grouped historyEntries to undo list
             *      after the session is created it gives access to the following methods
             *          UndoSession.getId() - return the sessionId of the current undo-session
             *          UndoSession.add(HistoryEntry) - adds entry to the session
             *          @see UndoSession
             *
             * returns {Object}
             */
            /**
             *
             * @returns {{getId: Function, add: Function}}
             */
            startSession: function startSession() {
                var undoSession = this.sessionManager.openSession();
                /**
                 * @lends {module.undoRedo.Session.prototype}
                 */
                return $.extend(undoSession, {
                    /**
                     *
                     * @param {String} type
                     * @param {Object} payload
                     * @param {String} message
                     * @param {Boolean=} isExecutable
                     */
                    add: function (type, payload, message, isExecutable) {
                        var
                            entryKey = this.database.createEntryKey(),
                            sessionId = undoSession.getId(),
                            sessionIndex,
                            historyEntry;

                        sessionIndex = undoSession.undoAdd(entryKey);
                        historyEntry = this.historyEntryFactory.build(type, payload, message, sessionId, sessionIndex, isExecutable);
                        historyEntry.setKey(entryKey);

                        return this.addClientEntry(historyEntry, sessionId, sessionIndex);
                    }.bind(this)
                });
            },

            /**
             * refreshes Ui with the data chains from history lists
             */
            refreshUi: function () {
                this.uiManager.handle(this.history.chains);
            },
            /**
             * Called from controllers to notify undoRedo engine that an entry was created server side
             *
             * @returns {Promise|Q.Promise}
             */
            onNewEntryServerSide: function onNewEntryServerSide() {
                var deferred = Q.defer();

                this.history.executeWhenReady(function () {
                    var chain = [];

                    chain.push(function () {
                        return this.closeActiveSession(true);
                    }.bind(this));

                    chain.push(function () {
                        return this.history.deleteRedoChain(/** ignoreServer */ true);
                    }.bind(this));

                    chain.push(function () {
                        return this.history.populateChains();
                    }.bind(this));

                    chain.reduce(Q.when, Q(null))
                        .then(function (res) {
                            deferred.resolve(res);
                        })
                        .catch(function (error) {
                            deferred.reject(error);
                            this.onError(error);
                        }.bind(this));

                    return deferred.promise;

                }.bind(this));

                return deferred.promise;
            },
            /**
             * called from pages structure to "hide" redo entries
             * @deprecated hack :: this behaviour will be fixed with undo-redo-pages-individual-changes
             */
            hideRedoQueue: function () {
                this.ui.refresh({
                    undoList: diy.editor.undoRedo.history.chains.undoChain,
                    redoList: [],
                    lastEntry: {}
                });
            },

            /**
             * Starts execution of an entry with given entryKey
             *      called from undoRedo.ui
             *
             * @param entryKey
             * @returns {Promise|Q.Promise}
             */
            executeEntry: function (entryKey) {
                var sequence = Q.defer();

                this.history.executeWhenReady(function executeEntrySequence() {
                    var
                        entry = this.history.getEntryByKey(entryKey),
                        execution = [],
                        deferred = Q.defer();

                    if (entry !== null && entry.isExecutable()) {
                        // close current session of executes from another
                        if (entry.getUndoSessionId() !== this.sessionManager.getActiveSessionId()) {
                            execution.push(this.closeActiveSession.bind(this));
                        }
                        // execute entry
                        execution.push(function executeEntry() {
                            return this.executor.execute(entry);
                        }.bind(this));

                        execution.reduce(Q.when, Q(true).timeout(constants.executionTimeout, constants.executionTimeoutMessage))
                            .then(function (ready) {
                                deferred.resolve(ready);
                                sequence.resolve(ready);
                            })
                            .catch(function (error) {
                                deferred.reject(error);
                                this.onError(error);
                                sequence.reject(error);
                            }.bind(this));
                    } else {
                        deferred.reject('no entry with given key');
                        sequence.reject('no entry with given key');
                        this.onError('no entry with given key');
                    }
                    return deferred.promise;
                }.bind(this));

                return sequence.promise;
            },

            /**
             * called from history entries to request a page reload to a given url
             * if url is undefined a reload event for the current page will be triggered
             *
             * @param url
             */
            requestBrowserReload: function (url) {
                this.browserReload.requested = true;
                if (url === undefined) {
                    this.browserReload.url = null;
                } else {
                    this.browserReload.url = url;
                }

                this.history.executeWhenReady(function executeBrowserReload() {
                    if (this.browserReload.requested === true) {
                        helper.browserReload(this.browserReload.url, null, 100);
                    }
                }.bind(this));
            },

            /**
             *
             * @returns {Promise|Q.Promise}
             */
            cleanUp: function () {
                return this.database.cleanup();
            },
            /**
             * called from controllers to temporary disable the possibility of executing displayed history entries
             * unblock event will be automatically triggered when:
             *  - an entry is created
             *  - signalEntryCreatedOnServerSide is called
             *  - anything that calls a ui.refresh event
             *
             * unblock can be done using unblockUserInterface method
             *
             */
            blockUserInterface: function () {
                this.ui.blockUi();
            },

            /**
             * Reverse method for blockUserInterface
             *
             * @see blockUserInterface
             */
            unblockUserInterface: function () {
                this.ui.unblockUi();
            }
        });

        return UndoRedo;
    });



/*****************
 ** WEBPACK FOOTER
 ** ./source/cdn/document_root/s/diy/undo-redo/App.js
 ** module id = 921
 ** module chunks = 0
 **/